import React from 'react';
import { motion } from 'framer-motion';
import { Braces, Shield, Cpu, Stethoscope } from 'lucide-react';

const focusAreas = [
    {
        title: "AI-native applications",
        description: "AI-native software that redefines workflows: copilots for legal/finance/healthcare, agentic ops, generative video and multimodal collaboration. Thesis: distribution via workflow replacement, not add-ons.",
        icon: <Braces className="w-7 h-7 text-white" />,
        iconBg: "bg-blue-500",
    },
    {
        title: "Developer tools and security",
        description: "Foundational tools that reduce time-to-value for builders: frameworks, testing, observability, policy-as-code, and runtime security. Thesis: monetization through usage-aligned pricing and enterprise readiness.",
        icon: <Shield className="w-7 h-7 text-white" />,
        iconBg: "bg-orange-500",
    },
    {
        title: "Infrastructure and compute",
        description: "Data labeling, retrieval, inference platforms, and next-gen compute including photonics and hardware–software co-design. Thesis: performance-to-cost breakthroughs shift entire application frontiers.",
        icon: <Cpu className="w-7 h-7 text-white" />,
        iconBg: "bg-green-500",
    },
    {
        title: "Healthcare and science",
        description: "ML-first discovery, trial acceleration, and care pathways with measurable outcomes. Thesis: data moats, clinical validation, and reimbursement alignment drive durable value.",
        icon: <Stethoscope className="w-7 h-7 text-white" />,
        iconBg: "bg-pink-500",
    }
];


const Focus = () => {
    const introText = "Our thesis is simple: we back deeply technical founders building in markets we understand, where our platform support can meaningfully accelerate the path to product-market fit.";

    return (
        <motion.div
            className="bg-background py-12 md:py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className="container mx-auto px-6">

                {/* --- Section Header: Centered --- */}
                {/* Added text-center and flex items-center */}
                <div className="max-w-6xl mx-auto mb-16 text-center flex flex-col items-center">
                    <motion.h1
                        className="text-4xl md:text-5xl font-bold text-foreground mb-6 font-serif"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                    >
                        Focus
                    </motion.h1>

                    {/* Intro Text Wrapper - Centered */}
                    {/* Replaced TextRevealOnScroll with motion.p */}
                    <motion.p
                        className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.4 }}
                    >
                        {introText}
                    </motion.p>
                </div>
                {/* --- End Section Header --- */}


                {/* Card Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                    {focusAreas.map((area, index) => (
                        <motion.div
                            key={area.title}
                            className="bg-card p-8 rounded-lg shadow-medium transition-all duration-500 border border-border hover:border-secondary/50 hover:shadow-strong group"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                            viewport={{ once: true, amount: 0.2 }}
                        >
                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${area.iconBg} mb-6`}>
                                {area.icon}
                            </div>
                            <h3 className="text-2xl font-bold text-card-foreground group-hover:text-primary mb-4 transition-colors duration-500">
                                {area.title}

                            </h3>
                            <p className="text-base text-muted-foreground leading-relaxed">{area.description}</p>
                            <div className="mt-6 h-1 w-0 bg-gradient-accent group-hover:w-full transition-all duration-500 rounded-full"></div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </motion.div>
    );
};

export default Focus;